import * as Tone from "tone";

import type { EnemyAttack } from "../content/levelTypes.ts";

type OscType = "triangle" | "square" | "sawtooth" | "sine";
type PadOsc = "fatsawtooth" | "fatsquare" | "fattriangle";
type NoiseType = "white" | "pink" | "brown";

interface LevelTheme {
  chords: readonly string[][];
  bass: readonly string[];
  /** 8 eighth-note steps per bar: 1 = bass hit on this step. */
  bassPattern: readonly number[];
  /** 8 eighth-note steps per bar: 1 = hat tick on this step. */
  hatPattern: readonly number[];
  /** 8 eighth-note steps per bar: 1 = kick thump on this step. */
  kickPattern: readonly number[];
  lead: readonly (string | null)[];
  /** Lead synth voice — a different timbre per level. */
  leadOsc: OscType;
  /** Pad voice: base wave + detune spread + envelope attack, per level. */
  padOsc: PadOsc;
  padSpread: number;
  padAttack: number;
  /** Bass voice wave per level. */
  bassOsc: OscType;
  /** Percussion character: hat noise colour + tick length, per level. */
  hatNoise: NoiseType;
  hatDecay: number;
  bpm: number;
  /** EDM sidechain pump: duck the pad on every kick hit (level 2's thing). */
  sidechain?: boolean;
}

/**
 * One song per level — and not just different notes through the same rig:
 * every level re-voices the whole band. Chord progression, pad wave/detune/
 * attack, bass wave + rhythm, hat noise colour + groove, kick pattern, lead
 * riff + timbre, and tempo all swap per level, so each stage has its own
 * genuinely distinct soundtrack. Only the layering/mix machinery (the
 * pad/bass/lead/hat/kick gain faders driven by intensity cues) is shared.
 */
const LEVEL_THEMES: readonly LevelTheme[] = [
  // Level 1 — INERTIA: the original song, untouched. Warm, flowing A-minor,
  // straight quarter-note bass, offbeat white hats, soft triangle lead, no
  // kick drum. The "learn to flow" song.
  {
    chords: [
      ["A2", "E3", "A3", "C4"],
      ["F2", "C3", "F3", "A3"],
      ["G2", "D3", "G3", "B3"],
      ["E2", "B2", "E3", "G3"],
    ],
    bass: ["A1", "A1", "F1", "G1"],
    bassPattern: [1, 0, 1, 0, 1, 0, 1, 0],
    hatPattern: [0, 1, 0, 1, 0, 1, 0, 1],
    kickPattern: [0, 0, 0, 0, 0, 0, 0, 0],
    lead: ["A4", "C5", "E5", "A5", "E5", "C5", "G4", "B4"],
    leadOsc: "triangle",
    padOsc: "fatsawtooth",
    padSpread: 24,
    padAttack: 1.6,
    bassOsc: "sine",
    hatNoise: "white",
    hatDecay: 0.05,
    bpm: 92,
  },
  // Level 2 — VECTOR: a real EDM banger, matching the eyeballs' laser fire
  // and the closing bomber's grand explosion. Four-on-the-floor kick with
  // the pad sidechain-pumping against it, a plucky supersaw stab instead of
  // a sustained pad, clipped pink-noise hats, a driving square bass, and a
  // stabbing two-bar square lead — at a proper 128 BPM. A riser + drop cue
  // (level2.ts) slams the whole mix wide open right as the bomber's satchel
  // detonates.
  {
    chords: [
      ["D2", "A2", "D3", "F3"],
      ["Bb1", "F2", "Bb2", "D3"],
      ["C2", "G2", "C3", "E3"],
      ["A1", "E2", "A2", "C3"],
    ],
    bass: ["D1", "D2", "Bb1", "C2"],
    bassPattern: [1, 0, 0, 1, 0, 1, 1, 0],
    hatPattern: [0, 1, 0, 1, 0, 1, 1, 1],
    kickPattern: [1, 0, 1, 0, 1, 0, 1, 0],
    lead: [
      "D5", null, "F5", "D5", "A5", null, "G5", "F5",
      "E5", null, "C5", "E5", "A4", null, "C5", "E5",
    ],
    leadOsc: "square",
    padOsc: "fatsquare",
    padSpread: 22,
    padAttack: 0.1, // plucky trance stab, not a sustained pad — reads the sidechain pump
    bassOsc: "square",
    hatNoise: "pink",
    hatDecay: 0.025,
    bpm: 128,
    sidechain: true,
  },
  // Level 3 — CARVE: the sword debut. Bright, heroic C-major on a warm
  // triangle organ pad, bouncing triangle bass, splashy white hats, a
  // skip-step kick, and a chiming two-bar sine-bell melody. The "go get
  // them" song.
  {
    chords: [
      ["C3", "G3", "C4", "E4"],
      ["G2", "D3", "G3", "B3"],
      ["A2", "E3", "A3", "C4"],
      ["F2", "C3", "F3", "A3"],
    ],
    bass: ["C2", "G1", "A1", "F1"],
    bassPattern: [1, 0, 1, 0, 1, 0, 1, 1],
    hatPattern: [0, 1, 0, 1, 1, 0, 1, 1],
    kickPattern: [1, 0, 0, 0, 1, 0, 0, 1],
    lead: [
      "C5", "D5", "E5", "G5", "A5", "G5", "E5", "D5",
      "C5", "E5", "G5", "C6", "A5", "G5", "E5", "D5",
    ],
    leadOsc: "sine",
    padOsc: "fattriangle",
    padSpread: 12,
    padAttack: 1.1,
    bassOsc: "triangle",
    hatNoise: "white",
    hatDecay: 0.09,
    bpm: 106,
  },
  // Level 4 — FLUX: the pre-boss gauntlet. Heavily detuned sawtooth churn
  // with a fast stabbing attack, growling sawtooth bass, long brown-noise
  // industrial hats, a broken stumbling kick, and a restless square lead.
  // The "storm" song.
  {
    chords: [
      ["B1", "F#2", "B2", "D3"],
      ["G1", "D2", "G2", "B2"],
      ["D2", "A2", "D3", "F#3"],
      ["A1", "E2", "A2", "C#3"],
    ],
    bass: ["B1", "B1", "G1", "A1"],
    bassPattern: [1, 1, 0, 1, 0, 1, 0, 1],
    hatPattern: [1, 0, 1, 1, 0, 1, 1, 1],
    kickPattern: [1, 0, 0, 1, 1, 0, 1, 0],
    lead: [
      "B4", "B4", null, "D5", "C#5", null, "B4", "F#5",
      "E5", null, "D5", "C#5", "D5", null, "F#5", "A5",
    ],
    leadOsc: "square",
    padOsc: "fatsawtooth",
    padSpread: 46,
    padAttack: 0.3,
    bassOsc: "sawtooth",
    hatNoise: "brown",
    hatDecay: 0.13,
    bpm: 112,
  },
  // Level 5 — PYRE: doom. Massive slow-swelling detuned square drone with a
  // menacing flat-6, galloping square bass, relentless pink hats, heavy
  // half-time kick, snarling sawtooth lead. The boss song (cues push the
  // tempo further as the dragon escalates).
  {
    chords: [
      ["E2", "B2", "E3", "G3"],
      ["C2", "G2", "C3", "Eb3"],
      ["A1", "E2", "A2", "C3"],
      ["F1", "C2", "F2", "Ab2"],
    ],
    bass: ["E1", "E1", "C1", "F1"],
    bassPattern: [1, 0, 1, 1, 1, 0, 1, 1],
    hatPattern: [1, 1, 1, 1, 1, 1, 1, 1],
    kickPattern: [1, 0, 0, 0, 1, 0, 1, 0],
    lead: [
      "E5", null, "E5", "G5", "F#5", null, "B4", null,
      "E5", "G5", "B5", "E6", "D5", null, "C5", "B4",
    ],
    leadOsc: "sawtooth",
    padOsc: "fatsquare",
    padSpread: 36,
    padAttack: 2.4,
    bassOsc: "square",
    hatNoise: "pink",
    hatDecay: 0.05,
    bpm: 118,
  },
];

/**
 * Procedural, adaptive soundtrack. There are no audio asset files — everything
 * is synthesized in-engine so the music can evolve in layers as the player
 * progresses (per the design doc's "evolving toolkit = evolving score").
 *
 * Layering model:
 *   - PAD: a slow, warm chord bed. Always on during play (the "trance" base).
 *   - BASS: a pulsing sub that fades in as intensity rises.
 *   - LEAD/HATS: rhythmic energy that ramps with intensity tiers 1-3.
 *
 * Each level swaps in its own LEVEL_THEMES entry (chords/bass/lead/bpm) via
 * `beginLevel(levelIdx)` — the running Tone.Sequences read note data through
 * `this.chords`/`this.bassNotes`/`this.leadNotes` each tick, so themes hot-swap
 * without ever stopping or rebuilding the transport (avoids Web Audio
 * scheduling errors from restarting sequences mid-session).
 *
 * Cues from the level timeline ("intensity:2", "boss:2", ...) drive the mix.
 * All Web Audio work is guarded: if the context can't start (e.g. headless),
 * the game still runs silently.
 */
export class AudioManager {
  private ready = false;
  private enabled = true;
  private started = false;

  private pad?: Tone.PolySynth;
  private bass?: Tone.MonoSynth;
  private lead?: Tone.Synth;
  private hat?: Tone.NoiseSynth;
  private kick?: Tone.MembraneSynth;
  // Dedicated SFX voices, kept separate from the music synths so rapid hits /
  // grazes never collide with sequenced notes (which throws in Web Audio).
  private sfxBlip?: Tone.PolySynth;
  private sfxNoise?: Tone.NoiseSynth;
  private sfxBoom?: Tone.MembraneSynth; // low impact thud (hits, enemy deaths)
  private sfxGrowl?: Tone.MonoSynth; // dragon roar glide
  private sfxTime = 0; // monotonic scheduling cursor for SFX
  private lastGraze = 0;
  /** Per-attack-kind throttle stamps so synced enemies don't double a sound. */
  private lastAttack = new Map<EnemyAttack, number>();
  private padGain?: Tone.Gain;
  private bassGain?: Tone.Gain;
  private leadGain?: Tone.Gain;
  private hatGain?: Tone.Gain;
  private kickGain?: Tone.Gain;
  private reverb?: Tone.Reverb;

  private padLoop?: Tone.Loop;
  private bassSeq?: Tone.Sequence<number>;
  private hatSeq?: Tone.Sequence<number>;
  private leadSeq?: Tone.Sequence<number>;
  private kickSeq?: Tone.Sequence<number>;

  // Active theme's note/rhythm data — read live by the running sequences each
  // tick, so `beginLevel()` can swap songs without touching the transport.
  private chords: readonly string[][] = LEVEL_THEMES[0].chords;
  private bassNotes: readonly string[] = LEVEL_THEMES[0].bass;
  private bassPattern: readonly number[] = LEVEL_THEMES[0].bassPattern;
  private hatPattern: readonly number[] = LEVEL_THEMES[0].hatPattern;
  private kickPattern: readonly number[] = LEVEL_THEMES[0].kickPattern;
  private leadNotes: readonly (string | null)[] = LEVEL_THEMES[0].lead;
  /** Whether the active theme wants the kick to sidechain-pump the pad. */
  private sidechainOn = false;
  /** Last commanded pad-gain target, so the sidechain duck knows where to
   * recover to (Tone.Gain doesn't expose an in-flight ramp's destination). */
  private padTarget = 1;
  private kickTarget = 0;

  /** Build the synth graph. Safe to call eagerly; produces no sound until resume(). */
  async init(): Promise<void> {
    if (this.ready) return;
    try {
      this.reverb = new Tone.Reverb({ decay: 6, wet: 0.35 }).toDestination();

      this.padGain = new Tone.Gain(0).connect(this.reverb);
      this.bassGain = new Tone.Gain(0).toDestination();
      this.leadGain = new Tone.Gain(0).connect(this.reverb);
      this.hatGain = new Tone.Gain(0).toDestination();
      this.kickGain = new Tone.Gain(0).toDestination();

      this.pad = new Tone.PolySynth(Tone.Synth, {
        oscillator: { type: "fatsawtooth", count: 3, spread: 24 },
        envelope: { attack: 1.6, decay: 0.4, sustain: 0.8, release: 3 },
        volume: -16,
      }).connect(this.padGain);

      this.bass = new Tone.MonoSynth({
        oscillator: { type: "sine" },
        envelope: { attack: 0.02, decay: 0.2, sustain: 0.5, release: 0.3 },
        filterEnvelope: { attack: 0.02, decay: 0.2, sustain: 0.4, baseFrequency: 120 },
        volume: -10,
      }).connect(this.bassGain);

      this.lead = new Tone.Synth({
        oscillator: { type: "triangle" },
        envelope: { attack: 0.01, decay: 0.18, sustain: 0.1, release: 0.2 },
        volume: -18,
      }).connect(this.leadGain);

      this.hat = new Tone.NoiseSynth({
        noise: { type: "white" },
        envelope: { attack: 0.001, decay: 0.05, sustain: 0 },
        volume: -24,
      }).connect(this.hatGain);

      // Music kick drum — its own voice (never shared with sfxBoom, so rapid
      // SFX can't collide with sequenced beats). Silent on levels whose theme
      // has an empty kickPattern (level 1 keeps its original kickless song).
      this.kick = new Tone.MembraneSynth({
        pitchDecay: 0.04,
        octaves: 6,
        envelope: { attack: 0.001, decay: 0.3, sustain: 0 },
        volume: -9,
      }).connect(this.kickGain);

      // SFX voices (always audible, independent of the adaptive mix gains).
      this.sfxBlip = new Tone.PolySynth(Tone.Synth, {
        oscillator: { type: "triangle" },
        envelope: { attack: 0.005, decay: 0.12, sustain: 0, release: 0.1 },
        volume: -14,
      }).connect(this.reverb);
      this.sfxNoise = new Tone.NoiseSynth({
        noise: { type: "pink" },
        envelope: { attack: 0.001, decay: 0.18, sustain: 0 },
        volume: -8,
      }).toDestination();
      this.sfxBoom = new Tone.MembraneSynth({
        pitchDecay: 0.06,
        octaves: 5,
        envelope: { attack: 0.001, decay: 0.4, sustain: 0 },
        volume: -6,
      }).toDestination();
      this.sfxGrowl = new Tone.MonoSynth({
        oscillator: { type: "sawtooth" },
        envelope: { attack: 0.04, decay: 0.5, sustain: 0.4, release: 0.5 },
        filterEnvelope: {
          attack: 0.05,
          decay: 0.6,
          sustain: 0.2,
          release: 0.4,
          baseFrequency: 90,
          octaves: 2.5,
        },
        volume: -10,
      }).connect(this.reverb);

      Tone.getTransport().bpm.value = 92;

      let chordIdx = 0;
      this.padLoop = new Tone.Loop((time) => {
        const chord = this.chords[chordIdx % this.chords.length];
        this.pad?.triggerAttackRelease(chord, "2m", time);
        chordIdx++;
      }, "2m");

      // Bass/hats step through 8 eighth-notes per bar, gated by the active
      // theme's rhythm pattern — so each level has its own groove, not just
      // its own notes.
      this.bassSeq = new Tone.Sequence(
        (time, i) => {
          if (!this.bassPattern[i % this.bassPattern.length]) return;
          const note = this.bassNotes[(i >> 1) % this.bassNotes.length];
          this.bass?.triggerAttackRelease(note, "8n", time);
        },
        [0, 1, 2, 3, 4, 5, 6, 7],
        "8n",
      );

      this.hatSeq = new Tone.Sequence(
        (time, i) => {
          if (this.hatPattern[i % this.hatPattern.length]) {
            this.hat?.triggerAttackRelease("16n", time);
          }
        },
        [0, 1, 2, 3, 4, 5, 6, 7],
        "8n",
      );

      this.kickSeq = new Tone.Sequence(
        (time, i) => {
          if (this.kickPattern[i % this.kickPattern.length]) {
            this.kick?.triggerAttackRelease("C1", "8n", time);
            // EDM sidechain pump: duck the pad on every audible kick hit.
            if (this.sidechainOn && this.kickTarget > 0.05) this.duckPad(time);
          }
        },
        [0, 1, 2, 3, 4, 5, 6, 7],
        "8n",
      );

      // Melodic arp that the adaptive mix fades in at high intensity (tier 3).
      // Indices into `this.leadNotes` (rather than the notes themselves) so a
      // level change can swap the melody without rebuilding the sequence.
      this.leadSeq = new Tone.Sequence<number>(
        (time, i) => {
          const note = this.leadNotes[i % this.leadNotes.length];
          if (note) this.lead?.triggerAttackRelease(note, "16n", time);
        },
        [0, 1, 2, 3, 4, 5, 6, 7],
        "8n",
      );

      this.ready = true;
    } catch (err) {
      console.warn("[audio] init failed; running silently", err);
      this.ready = false;
    }
  }

  /** Resume the AudioContext (must be called from a user gesture) and start play. */
  async resume(): Promise<void> {
    if (!this.enabled) return;
    try {
      await this.init();
      if (!this.ready) return;
      await Tone.start();
      if (!this.started) {
        this.padLoop?.start(0);
        this.bassSeq?.start(0);
        this.hatSeq?.start(0);
        this.leadSeq?.start(0);
        this.kickSeq?.start(0);
        Tone.getTransport().start();
        this.started = true;
      }
    } catch (err) {
      console.warn("[audio] resume failed", err);
    }
  }

  setEnabled(on: boolean): void {
    this.enabled = on;
    const t = Tone.getDestination?.();
    if (t) t.mute = !on;
  }

  get isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Load the given level's song — notes, rhythms, AND instrumentation (pad
   * wave/attack, bass wave, hat noise colour, kick pattern, lead voice,
   * tempo) — and reset the mix to the start-of-level base layer (just the
   * pad; other layers fade in via intensity cues). Called once per level
   * load — safe to call before audio is ready (it just won't be audible
   * until `resume()`).
   */
  beginLevel(levelIdx = 0): void {
    const theme = LEVEL_THEMES[Math.max(0, Math.min(LEVEL_THEMES.length - 1, levelIdx))];
    this.chords = theme.chords;
    this.bassNotes = theme.bass;
    this.bassPattern = theme.bassPattern;
    this.hatPattern = theme.hatPattern;
    this.kickPattern = theme.kickPattern;
    this.leadNotes = theme.lead;
    this.sidechainOn = theme.sidechain ?? false;
    if (this.ready) {
      try {
        Tone.getTransport().bpm.rampTo(theme.bpm, 1.5);
        if (this.lead) this.lead.oscillator.type = theme.leadOsc;
        // Re-voice the band for this level's song.
        this.pad?.set({
          oscillator: { type: theme.padOsc, count: 3, spread: theme.padSpread },
          envelope: { attack: theme.padAttack },
        });
        this.bass?.set({ oscillator: { type: theme.bassOsc } });
        this.hat?.set({
          noise: { type: theme.hatNoise },
          envelope: { decay: theme.hatDecay },
        });
      } catch {
        /* ignore */
      }
    }
    this.rampPad(1, 2);
    this.ramp(this.bassGain, 0, 0.5);
    this.ramp(this.leadGain, 0, 0.5);
    this.ramp(this.hatGain, 0, 0.5);
    this.rampKick(0, 0.5);
  }

  /** Interpret a timeline music cue and adjust the adaptive mix. */
  cue(name: string): void {
    if (!this.ready) return;
    switch (name) {
      case "intensity:1":
        this.ramp(this.bassGain, 0.7, 2);
        this.rampKick(0.6, 2);
        break;
      case "intensity:2":
        this.ramp(this.bassGain, 1, 1.5);
        this.ramp(this.hatGain, 0.6, 2);
        this.rampKick(0.85, 1.5);
        break;
      case "intensity:3":
        this.ramp(this.hatGain, 1, 1.5);
        this.ramp(this.leadGain, 0.8, 2);
        this.rampKick(1, 1.5);
        break;
      // Boss fight: bring the bass in immediately (bpm baseline for the boss
      // song is already set by beginLevel; these ramp it further per phase).
      case "phase:boss":
        this.ramp(this.bassGain, 0.8, 2);
        this.rampKick(0.7, 2);
        break;
      case "boss:2":
        this.setBpm(124);
        this.ramp(this.bassGain, 1, 1.5);
        this.ramp(this.hatGain, 0.8, 1.5);
        this.rampKick(0.9, 1.5);
        break;
      case "boss:3":
        this.setBpm(130);
        this.ramp(this.hatGain, 1, 1);
        this.ramp(this.leadGain, 0.9, 1.5);
        this.rampKick(1, 1);
        break;
      case "boss:frenzy":
        // Everything at full tilt for the dragon's last stand.
        this.setBpm(138);
        this.ramp(this.bassGain, 1, 1);
        this.ramp(this.hatGain, 1, 1);
        this.ramp(this.leadGain, 1, 1);
        this.rampKick(1, 1);
        break;
      case "phase:clear":
        // Full wind-down: strip everything back to the pad bed.
        this.ramp(this.leadGain, 0, 1.5);
        this.ramp(this.hatGain, 0, 1.5);
        this.rampKick(0, 1.5);
        this.ramp(this.bassGain, 0, 2.5);
        break;
      // EDM build-up: a pitch-rising riser leading into a drop — used by
      // Level 2 to line the music up with the closing bomber's satchel.
      case "riser":
        this.sfxRiser(4);
        break;
      case "drop":
        this.sfxDrop();
        // The drop hits: everything slams open almost instantly.
        this.ramp(this.bassGain, 1, 0.06);
        this.rampKick(1, 0.06);
        this.ramp(this.hatGain, 1, 0.06);
        this.ramp(this.leadGain, 1, 0.06);
        break;
    }
  }

  private setBpm(bpm: number): void {
    try {
      Tone.getTransport().bpm.rampTo(bpm, 2);
    } catch {
      /* ignore */
    }
  }

  // --- SFX -----------------------------------------------------------------
  /** Strictly-increasing time so overlapping SFX never collide in Web Audio. */
  private nextSfxTime(): number {
    const now = Tone.now();
    this.sfxTime = Math.max(now, this.sfxTime + 0.004);
    return this.sfxTime;
  }

  sfxGraze(): void {
    if (!this.ready || !this.started || !this.sfxBlip) return;
    const now = Tone.now();
    if (now - this.lastGraze < 0.05) return; // throttle dense graze streams
    this.lastGraze = now;
    try {
      this.sfxBlip.triggerAttackRelease("C6", "32n", this.nextSfxTime(), 0.35);
    } catch {
      /* ignore */
    }
  }

  sfxHit(): void {
    if (!this.ready || !this.started) return;
    try {
      this.sfxNoise?.triggerAttackRelease("8n", this.nextSfxTime());
      this.sfxBoom?.triggerAttackRelease("C2", "8n", this.nextSfxTime());
      this.sfxBlip?.triggerAttackRelease("A2", "8n", this.nextSfxTime(), 0.6);
    } catch {
      /* ignore */
    }
  }

  /** Soft two-note chirp announcing an enemy warping in. */
  sfxEnemySpawn(): void {
    if (!this.ready || !this.started || !this.sfxBlip) return;
    try {
      this.sfxBlip.triggerAttackRelease("E5", "32n", this.nextSfxTime(), 0.2);
      this.sfxBlip.triggerAttackRelease("B5", "32n", this.nextSfxTime() + 0.06, 0.16);
    } catch {
      /* ignore */
    }
  }

  /**
   * Per-attack enemy sound effects — each attack has its own voice:
   *   arrow  — quick high "thwip" as each archer dart looses
   *   bomb   — heavy underhand "whomp" as the five-bomb satchel flies
   *   hop    — bright two-note "cluck-boing" as the hen launches
   *   charge — low growl-and-thud as the beast commits to its charge
   *   laser  — descending "pew" zap as the eyeball's beam fires
   *   dash   — airy whoosh as the ninja flips into its dash
   */
  sfxEnemyAttack(kind: EnemyAttack): void {
    if (!this.ready || !this.started) return;
    // Throttle per kind: simultaneous same-kind attacks play once.
    const now = Tone.now();
    const last = this.lastAttack.get(kind) ?? -1;
    if (now - last < 0.07) return;
    this.lastAttack.set(kind, now);
    try {
      const t = this.nextSfxTime();
      switch (kind) {
        case "arrow":
          this.sfxBlip?.triggerAttackRelease("D6", "64n", t, 0.22);
          this.sfxNoise?.triggerAttackRelease("64n", t + 0.005);
          break;
        case "bomb":
          this.sfxBoom?.triggerAttackRelease("E2", "8n", t);
          this.sfxNoise?.triggerAttackRelease("16n", t + 0.02);
          this.sfxBlip?.triggerAttackRelease("G3", "16n", t + 0.03, 0.3);
          break;
        case "hop":
          this.sfxBlip?.triggerAttackRelease("G4", "32n", t, 0.25);
          this.sfxBlip?.triggerAttackRelease("C5", "32n", t + 0.07, 0.3);
          break;
        case "charge": {
          this.sfxGrowl?.triggerAttackRelease("G1", 0.45, t);
          this.sfxGrowl?.frequency.rampTo("D1", 0.4, t);
          this.sfxBoom?.triggerAttackRelease("A1", "8n", t + 0.03);
          break;
        }
        case "laser":
          this.sfxBlip?.triggerAttackRelease("C6", "32n", t, 0.4);
          this.sfxBlip?.triggerAttackRelease("F5", "32n", t + 0.05, 0.32);
          this.sfxNoise?.triggerAttackRelease("32n", t + 0.01);
          break;
        case "dash":
          this.sfxNoise?.triggerAttackRelease("8n", t);
          this.sfxBlip?.triggerAttackRelease("A5", "64n", t + 0.04, 0.15);
          break;
      }
    } catch {
      /* ignore */
    }
  }

  /** Enemy death/split: descending chirp over a thud. */
  sfxEnemyDeath(): void {
    if (!this.ready || !this.started) return;
    try {
      const t = this.nextSfxTime();
      this.sfxBoom?.triggerAttackRelease("G2", "16n", t);
      this.sfxBlip?.triggerAttackRelease("A4", "32n", t + 0.01, 0.35);
      this.sfxBlip?.triggerAttackRelease("D4", "32n", t + 0.08, 0.3);
    } catch {
      /* ignore */
    }
  }

  /** Meteor destroyed: fiery boom + crackling embers — distinct from other enemy deaths. */
  sfxMeteorDeath(): void {
    if (!this.ready || !this.started) return;
    try {
      const t = this.nextSfxTime();
      this.sfxBoom?.triggerAttackRelease("D2", "8n", t);
      this.sfxNoise?.triggerAttackRelease("4n", t + 0.01);
      // Crackle: a few stuttering noise ticks as the burning rock breaks apart.
      for (let i = 0; i < 4; i++) {
        this.sfxNoise?.triggerAttackRelease("32n", t + 0.06 + i * 0.045);
      }
      this.sfxBlip?.triggerAttackRelease("C4", "16n", t + 0.02, 0.4);
      this.sfxBlip?.triggerAttackRelease("F3", "16n", t + 0.16, 0.3);
    } catch {
      /* ignore */
    }
  }

  /** Dragon roar: low sawtooth glide with a noise swell underneath. */
  sfxRoar(): void {
    if (!this.ready || !this.started || !this.sfxGrowl) return;
    try {
      const t = this.nextSfxTime();
      this.sfxGrowl.triggerAttackRelease("C2", 0.7, t);
      this.sfxGrowl.frequency.rampTo("G1", 0.6, t);
      this.sfxNoise?.triggerAttackRelease("4n", t + 0.02);
      this.sfxBoom?.triggerAttackRelease("A1", "4n", t + 0.04);
    } catch {
      /* ignore */
    }
  }

  /** EDM build-up: a pitch-rising growl sweep leading into a drop. */
  sfxRiser(duration = 4): void {
    if (!this.ready || !this.started || !this.sfxGrowl) return;
    try {
      const t = this.nextSfxTime();
      this.sfxGrowl.triggerAttack("A1", t);
      this.sfxGrowl.frequency.rampTo("A4", duration, t);
      this.sfxGrowl.triggerRelease(t + duration);
      // The riser occupies the shared SFX cursor for its whole run so no
      // other SFX gets scheduled underneath it out of order.
      this.sfxTime = t + duration + 0.05;
    } catch {
      /* ignore */
    }
  }

  /** The drop hits: a heavy sub thump + noise crack + a bright chord stab. */
  sfxDrop(): void {
    if (!this.ready || !this.started) return;
    try {
      const t = this.nextSfxTime();
      this.sfxBoom?.triggerAttackRelease("C1", "2n", t);
      this.sfxNoise?.triggerAttackRelease("4n", t + 0.01);
      this.sfxBlip?.triggerAttackRelease(["A3", "C4", "E4"], "8n", t + 0.02, 0.5);
    } catch {
      /* ignore */
    }
  }

  /** Sword swing: short air-cut swoosh. */
  sfxSlash(): void {
    if (!this.ready || !this.started) return;
    try {
      this.sfxNoise?.triggerAttackRelease("16n", this.nextSfxTime());
      this.sfxBlip?.triggerAttackRelease("E6", "64n", this.nextSfxTime(), 0.12);
    } catch {
      /* ignore */
    }
  }

  /** Sword connecting with an enemy: satisfying thunk. */
  sfxSlashHit(): void {
    if (!this.ready || !this.started) return;
    try {
      const t = this.nextSfxTime();
      this.sfxBoom?.triggerAttackRelease("G3", "16n", t);
      this.sfxBlip?.triggerAttackRelease("B4", "32n", t + 0.01, 0.4);
    } catch {
      /* ignore */
    }
  }

  /** Heart pickup: bright ascending sparkle. */
  sfxHeart(): void {
    if (!this.ready || !this.started || !this.sfxBlip) return;
    try {
      const t = this.nextSfxTime();
      this.sfxBlip.triggerAttackRelease("E5", "32n", t, 0.35);
      this.sfxBlip.triggerAttackRelease("A5", "32n", t + 0.07, 0.4);
      this.sfxBlip.triggerAttackRelease("C6", "16n", t + 0.14, 0.45);
    } catch {
      /* ignore */
    }
  }

  /** Bomb detonation: heavy boom + noise. */
  sfxExplosion(): void {
    if (!this.ready || !this.started) return;
    try {
      const t = this.nextSfxTime();
      this.sfxBoom?.triggerAttackRelease("C1", "4n", t);
      this.sfxNoise?.triggerAttackRelease("8n", t + 0.01);
    } catch {
      /* ignore */
    }
  }

  /** Little rising fanfare on level clear. */
  sfxClear(): void {
    if (!this.ready || !this.started || !this.sfxBlip) return;
    try {
      const t = this.nextSfxTime();
      this.sfxBlip.triggerAttackRelease("A4", "16n", t, 0.4);
      this.sfxBlip.triggerAttackRelease("C5", "16n", t + 0.1, 0.4);
      this.sfxBlip.triggerAttackRelease("E5", "16n", t + 0.2, 0.45);
      this.sfxBlip.triggerAttackRelease("A5", "8n", t + 0.3, 0.5);
    } catch {
      /* ignore */
    }
  }

  fadeOut(seconds = 1.5): void {
    this.rampPad(0, seconds);
    this.ramp(this.bassGain, 0, seconds);
    this.ramp(this.leadGain, 0, seconds);
    this.ramp(this.hatGain, 0, seconds);
    this.rampKick(0, seconds);
  }

  private ramp(g: Tone.Gain | undefined, value: number, seconds: number): void {
    if (!g) return;
    try {
      g.gain.rampTo(value, seconds);
    } catch {
      /* ignore */
    }
  }

  /** Ramp the kick layer and remember the target (the sidechain duck needs
   * to know whether the kick is actually audible, and the pad ramp needs to
   * know where to recover to). */
  private rampKick(value: number, seconds: number): void {
    this.kickTarget = value;
    this.ramp(this.kickGain, value, seconds);
  }

  private rampPad(value: number, seconds: number): void {
    this.padTarget = value;
    this.ramp(this.padGain, value, seconds);
  }

  /** Classic EDM sidechain pump: snap the pad down, then let it recover —
   * timed to a kick hit so the pad breathes with the beat. */
  private duckPad(time: number): void {
    const g = this.padGain;
    if (!g) return;
    try {
      const floor = this.padTarget * 0.22;
      g.gain.cancelScheduledValues(time);
      g.gain.setValueAtTime(floor, time);
      g.gain.linearRampToValueAtTime(this.padTarget, time + 0.24);
    } catch {
      /* ignore */
    }
  }
}
